import { useCallback, useRef, useState } from 'react';
import type { ArtefactFileRow, ArtefactUploadVm } from '../useArtefactUpload';
import { CLIENT_ALLOWED_EXT, SUPPORTING_DOCS_CAP } from '../useArtefactUpload';

/**
 * ArtefactFileInput — UP-F08a/F09/F10/F11: the FILE case of the artefact capture step (C1's own).
 *
 * A drag-and-drop zone with a click-to-browse fallback (a hidden multiple <input type="file">), then
 * one removable ROW per added file (UP-F09). Exactly one row is the primary artefact and the rest are
 * supporting documents (up to five); a radio per row moves the primary mark, enforced client-side with
 * the DB unique index as the backstop. Each row shows a DETERMINATE progress bar while its bytes
 * stream (UP-F11, driven by the vm's XHR upload-progress), and a per-row NON-DESTRUCTIVE error with a
 * Retry that leaves every other row's input untouched (UP-F11a).
 *
 * The client checks extension + size before uploading (UP-F10/F11) as friendly pre-validation only —
 * the vm's clientFileReason marks a bad file as an error row that never uploads. The SERVER is the
 * real gate (it re-checks the allow/block lists AND re-sniffs the magic bytes from disk), so this
 * never claims to be the guarantee.
 *
 * No design frame draws this — the artefact pipeline is a parallel V3 flow with no SVG — so it is
 * composed from the same tokens the measured pages use (border-subtle, accent-subtle, the field
 * radii/insets) rather than reproduced from a mockup.
 */

/** Human-readable file size, coarse enough for a row label (no locale fuss). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export interface ArtefactFileInputProps {
  vm: ArtefactUploadVm;
}

export function ArtefactFileInput({ vm }: ArtefactFileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const { addFiles, removeFile, setPrimary, retryFile, files } = vm;
  const supportingCount = files.filter((f) => f.role === 'supporting').length;
  const atCap = supportingCount >= SUPPORTING_DOCS_CAP && files.some((f) => f.role === 'primary');

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
      // Reset so picking the SAME file again still fires onChange (e.g. after a remove).
      e.target.value = '';
    },
    [addFiles],
  );

  return (
    <div className="flex flex-col gap-5">
      {/* The drop zone + browse. The whole box is a button-like affordance; keyboard users open the
          picker with Enter/Space, drag users drop onto it. */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Add files — drag and drop, or press to browse"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={[
          'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed px-5 py-8 text-center',
          'cursor-pointer transition-colors duration-micro ease-in-out',
          dragOver
            ? 'border-control bg-accent-subtle'
            : 'border-border-subtle bg-transparent hover:bg-[rgb(0_157_154_/_0.06)]',
        ].join(' ')}
      >
        <span className="text-sm font-light text-ink">
          Drag a file here, or <span className="underline underline-offset-2">browse</span>
        </span>
        <span className="text-xs font-light text-ink/60">
          One primary artefact plus up to {SUPPORTING_DOCS_CAP} supporting documents. Allowed:{' '}
          {CLIENT_ALLOWED_EXT.join(', ')}. Up to 250 MB each.
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={onPick}
          // The client hint accepts the allowed extensions; the server remains the authority.
          accept={CLIENT_ALLOWED_EXT.map((e) => `.${e}`).join(',')}
        />
      </div>

      {atCap ? (
        <p className="text-xs font-light text-ink/60">
          Supporting-document limit reached ({SUPPORTING_DOCS_CAP}). Remove one to add another.
        </p>
      ) : null}

      {/* The rows. Primary first, then supporting in the order added. */}
      {files.length > 0 ? (
        <ul className="flex flex-col gap-3" aria-label="Selected files">
          {files.map((row) => (
            <FileRow
              key={row.id}
              row={row}
              onSetPrimary={() => setPrimary(row.id)}
              onRemove={() => removeFile(row.id)}
              onRetry={() => retryFile(row.id)}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FileRow({
  row,
  onSetPrimary,
  onRemove,
  onRetry,
}: {
  row: ArtefactFileRow;
  onSetPrimary: () => void;
  onRemove: () => void;
  onRetry: () => void;
}) {
  const pct = Math.round(row.progress * 100);
  const isError = row.status === 'error';
  return (
    <li
      className={[
        'flex flex-col gap-2 rounded-md border border-solid px-5 py-3',
        isError ? 'border-error' : 'border-border-subtle',
      ].join(' ')}
    >
      <div className="flex items-center gap-4">
        {/* Primary radio (UP-F09): exactly one primary. Grouped by name so the browser enforces
            single-select too; the vm is the source of truth. */}
        <label className="flex shrink-0 items-center gap-2 text-xs font-light text-ink">
          <input
            type="radio"
            name="artefact-primary"
            checked={row.role === 'primary'}
            onChange={onSetPrimary}
            aria-label={`Make ${row.file.name} the primary artefact`}
          />
          <span>{row.role === 'primary' ? 'Primary' : 'Supporting'}</span>
        </label>

        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-xs font-medium text-ink" title={row.file.name}>
            {row.file.name}
          </span>
          <span className="text-xs font-light text-ink/60">
            {formatBytes(row.file.size)}
            {row.status === 'uploading' ? ` — uploading ${pct}%` : ''}
            {row.status === 'uploaded' ? ' — ready' : ''}
          </span>
        </div>

        {/* Retry only on a transport failure the row can recover from (UP-F11a). */}
        {isError ? (
          <button
            type="button"
            onClick={onRetry}
            className="shrink-0 text-xs font-light text-ink underline underline-offset-2"
          >
            Retry
          </button>
        ) : null}
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${row.file.name}`}
          className="shrink-0 text-sm font-light text-ink/60 hover:text-ink"
        >
          ✕
        </button>
      </div>

      {/* Determinate progress bar (UP-F11) — shown while uploading and on a completed upload. */}
      {row.status === 'uploading' || row.status === 'uploaded' ? (
        <div
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Upload progress for ${row.file.name}`}
          className="h-1.5 w-full overflow-hidden rounded-full bg-border-subtle"
        >
          <div
            className="h-full rounded-full bg-control transition-[width] duration-micro ease-in-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}

      {/* Non-destructive per-row error message (UP-F11a) — never removes the row or its siblings. */}
      {isError && row.error ? <p className="text-xs font-light text-error">{row.error}</p> : null}
    </li>
  );
}
